import to from "@/lib/to";

export class TranscriptionSession {
    #transcriptionConnection = null

    /**
     * Starts a transcription session for the given patient monitor ID and audio input stream.
     * @param {string} patientMonitorId ID of the associated patient monitor (needed for API call to start transcription)
     * @param {MediaStream} audioInputStream The stream to transcribe
     * @param {string} speakerRole The role of the speaker ('patient' or 'caregiver')
     * @param {function(string)} transcriptionLineCallback A callback function that will be called with each transcription line
     * @param {object} httpClient HTTP client for making API requests (e.g., Vue's this.$http)
     */
    async start(patientMonitorId, audioInputStream, speakerRole, transcriptionLineCallback, httpClient) {
        console.info('Starting transcription session...');

        if (!audioInputStream) {
            throw new Error('No audio input stream available for transcription');
        }

        if (!patientMonitorId) {
            throw new Error('No patient monitor ID available for transcription');
        }

        if (!speakerRole || (speakerRole !== 'patient' && speakerRole !== 'caregiver')) {
            throw new Error('Invalid speaker role for transcription. Must be "patient" or "caregiver".');
        }

        this.#transcriptionConnection = new RTCPeerConnection({
            iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });
        audioInputStream.getTracks().forEach(track => {
            console.debug(`adding audio track ${track.id} (${track.enabled}) for transcription`);
            this.#transcriptionConnection.addTrack(track, audioInputStream);
        });
        const dc = this.#transcriptionConnection.createDataChannel('oai-events');
        const offer = await this.#transcriptionConnection.createOffer();
        await this.#transcriptionConnection.setLocalDescription(offer);
        dc.addEventListener('message', (e) => {
            const data = JSON.parse(e.data);
            // see https://platform.openai.com/docs/guides/realtime-transcription#handling-transcriptions
            if (data.type === 'conversation.item.input_audio_transcription.completed') {
                const transcript = data.transcript;

                if (transcript && transcript.trim().length > 0) {
                    transcriptionLineCallback(transcript);
                }
            }
        });

        const [err, response] = await to(httpClient.post('/videos/transcription_session', {
            sdp: offer.sdp,
            patient_monitor_id: patientMonitorId,
            speaker_role: speakerRole
        }));
        if (err) {
            console.error('Failed to create transcription session:', err);
            return;
        }
        console.info("Transcription session response:", response.data);
        await this.#transcriptionConnection.setRemoteDescription({
            type: 'answer',
            sdp: response.data.sdp
        });

        console.log('Transcription session initialized');
    }

    stop() {
        if (this.#transcriptionConnection) {
            this.#transcriptionConnection.close();
            this.#transcriptionConnection = null;
            console.info('Transcription session stopped');
        }
    }
}